/*
 * Floating pill navigation — backdrop measurement.
 *
 * Paints the first content section's background colour up behind the ticker and
 * the nav pill, so the pill reads as floating above the page rather than
 * sitting on a white strip. The band itself is a ::before on the first section
 * (see nav-pill.css); this file only supplies the two values CSS can't work out
 * on its own:
 *
 *   --nav-stack-h    height of the ticker + pill + their gaps
 *   --nav-backdrop   the first section's effective background colour
 *
 * Both need measuring rather than hardcoding: the ticker is injected by a
 * third-party app after DOM ready and its height follows an app setting, and
 * the first section's colour comes from a merchant-set inline style on a
 * descendant, not on the section wrapper.
 *
 * Pairs with nav-pill.css. Degrades to no band if anything is missing.
 */
(function () {
  var GROUP = '.shopify-section-group-group-header';
  var group = document.querySelector(GROUP);
  var pill = document.querySelector(GROUP + ' .theme__header');
  var main = document.querySelector('main.main-content');
  if (!group || !pill || !main) return;

  var firstSection = main.querySelector(':scope > .shopify-section');
  var root = document.documentElement;

  /*
   * nav-pill.css holds the ticker's height open before the bar exists, so that
   * the pill doesn't drop when the app injects it. That height has to be a
   * constant in CSS, but the app owns the font size inside the bar — so record
   * what the bar really measures and let layout/theme.liquid apply it before
   * the next page paints. One page view of self-correction beats a stale
   * constant shifting the layout on every load.
   */
  var TICKER_KEY = 'ls:ticker-h';

  function rememberTickerHeight(height) {
    var value = String(Math.round(height));
    if (value === '0') return;
    try {
      if (localStorage.getItem(TICKER_KEY) !== value) localStorage.setItem(TICKER_KEY, value);
    } catch (e) {
      /* Private mode or a full quota. The CSS constant still applies. */
    }
  }

  function forgetTickerHeight() {
    try {
      localStorage.removeItem(TICKER_KEY);
    } catch (e) {}
  }

  function px(el, prop) {
    return parseFloat(getComputedStyle(el)[prop]) || 0;
  }

  function isOpaque(color) {
    if (!color) return false;
    var m = color.match(/^rgba?\(([^)]+)\)$/);
    if (!m) return false;
    var parts = m[1].split(',');
    return parts.length < 4 || parseFloat(parts[3]) > 0.95;
  }

  /*
   * The section wrapper is usually transparent and the colour lives on an inner
   * element. Take the first full-width, top-aligned descendant that actually
   * paints something, which is the element whose background the band has to
   * continue.
   */
  function backdropColor() {
    if (!firstSection) return null;
    var box = firstSection.getBoundingClientRect();
    if (!box.width) return null;

    var candidates = [firstSection];
    var descendants = firstSection.querySelectorAll('*');
    for (var i = 0; i < descendants.length && i < 40; i++) candidates.push(descendants[i]);

    for (var j = 0; j < candidates.length; j++) {
      var el = candidates[j];
      var rect = el.getBoundingClientRect();
      // Must span the section and start at its top, or it isn't the backdrop.
      if (rect.width < box.width - 1 || rect.top > box.top + 1 || rect.height < 1) continue;

      var style = getComputedStyle(el);

      /*
       * A colour the element isn't actually showing is not the backdrop. The
       * theme's image overlays are the case that matters: .image-overlay keeps
       * an opaque near-black background-color and dials it down with `opacity`,
       * so on any page whose first section carries one — the cart, for one —
       * this used to hand back #101820 and paint a black bar behind the nav.
       */
      if (style.visibility === 'hidden' || parseFloat(style.opacity) < 0.95) continue;

      if (isOpaque(style.backgroundColor)) return style.backgroundColor;
    }
    return null;
  }

  function measure() {
    var ticker = group.querySelector('.ss-announcement-bar');
    var gapTop = px(pill, 'marginTop');
    var height = 0;

    if (ticker) {
      rememberTickerHeight(ticker.offsetHeight);
      height += px(ticker, 'marginTop') + ticker.offsetHeight;
      // Adjacent siblings in normal flow, so these two margins collapse.
      height += Math.max(px(ticker, 'marginBottom'), gapTop);
    } else {
      height += gapTop;
    }
    height += pill.offsetHeight + px(pill, 'marginBottom');

    // Round up: the pill's height is fractional, and falling a sub-pixel short
    // leaves a hairline of page background above the band. Overshooting just
    // extends the band above the top of the document, where it can't be seen.
    root.style.setProperty('--nav-stack-h', Math.ceil(height) + 1 + 'px');

    /*
     * Where the pill's bottom edge sits once it is stuck — i.e. with the ticker
     * scrolled away. Sticky page furniture (the product page's info column)
     * offsets from this so it parks below the pill instead of under it.
     */
    root.style.setProperty('--nav-stuck-h', Math.ceil(gapTop + pill.offsetHeight) + 'px');

    var color = backdropColor();
    if (color) root.style.setProperty('--nav-backdrop', color);
    else root.style.removeProperty('--nav-backdrop');
  }

  measure();

  if (window.ResizeObserver) {
    var ro = new ResizeObserver(measure);
    ro.observe(pill);
    if (firstSection) ro.observe(firstSection);
    // The ticker doesn't exist yet on first run; observe it once it lands.
    var mo = new MutationObserver(function () {
      var ticker = group.querySelector('.ss-announcement-bar');
      if (ticker) {
        ro.observe(ticker);
        mo.disconnect();
      }
      measure();
    });
    mo.observe(group, {childList: true, subtree: true});
  } else {
    window.addEventListener('resize', measure);
  }

  /*
   * By `load` the app has long since had its chance to inject the bar — it does
   * so on DOMContentLoaded. If it hasn't, the space nav-pill.css is holding for
   * it would stay held forever, so give it back and stop replaying the cached
   * height on future page views.
   */
  window.addEventListener('load', function () {
    if (!group.querySelector('.ss-announcement-bar')) {
      root.style.setProperty('--ticker-h', '0px');
      forgetTickerHeight();
    }
    measure();
  });

  document.addEventListener('shopify:section:load', measure);
  document.addEventListener('shopify:section:unload', measure);
})();
